import type { MessageDescriptor } from 'react-intl';

import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Box, xcss } from '@atlaskit/primitives';
import { SimpleTag as Tag } from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';

import {
  messages,
  TestCaseAttribute,
} from 'src/components/pipelines/components/test-management';
import { UpdateTestCaseStatusParams } from 'src/components/pipelines/hooks/use-test-management-actions';
import { useAnalytics } from 'src/components/settings/analytics';

const dropdownMenuTagStyles = xcss({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: 'border.width',
  borderStyle: 'solid',
  borderRadius: 'radius.small',
  backgroundColor: 'color.background.neutral.subtle',
  color: 'color.text',
  font: 'font.body',
  cursor: 'pointer',
});

const dropdownMenuTagSpacingStyles = xcss({
  height: token('space.250'),
  paddingInline: 'space.050',
});

const headerDropdownMenuTagSpacingStyles = xcss({
  height: token('space.400'),
  paddingInline: 'space.100',
});

const dropdownMenuPanelStyles = xcss({
  minWidth: '200px',
  maxWidth: '305px',
});

const chevronIconWrapperStyles = xcss({
  marginInlineStart: 'space.100',
});

type TestStateDropdownProps = {
  testUuid: string;
  isHeaderAction?: boolean;
  attributes: TestCaseAttribute[];
  isTestCaseUpdating: boolean;
  updateTestCaseStatus: (params: UpdateTestCaseStatusParams) => void;
  formatMessage: (message: MessageDescriptor) => string;
};

export const TestStateDropdown = ({
  testUuid,
  isHeaderAction = false,
  attributes,
  isTestCaseUpdating,
  updateTestCaseStatus,
  formatMessage,
}: TestStateDropdownProps) => {
  const { publishButtonClickEvent } = useAnalytics({
    source: 'testStateDropdown',
  });

  const isFlaky = attributes.includes(TestCaseAttribute.FLAKY);

  const handleStatusChange = (setFlaky: boolean) => {
    if (setFlaky !== isFlaky) {
      updateTestCaseStatus({
        testUuid,
        attributes: setFlaky ? [TestCaseAttribute.FLAKY] : [],
      });
      publishButtonClickEvent('toggleFlakyStatus', {
        action: setFlaky ? 'markedFlaky' : 'unmarkedFlaky',
      });
    }
  };

  const borderColor = isFlaky
    ? token('color.background.warning.bold')
    : token('color.border');

  return (
    <DropdownMenu
      trigger={({ triggerRef, ...props }) => (
        <Box
          as={'button' as any}
          type="button"
          ref={triggerRef}
          xcss={[
            dropdownMenuTagStyles,
            isHeaderAction
              ? headerDropdownMenuTagSpacingStyles
              : dropdownMenuTagSpacingStyles,
          ]}
          style={{ borderColor }}
          disabled={isTestCaseUpdating}
          {...props}
        >
          {isFlaky ? formatMessage(messages.flaky) : formatMessage(messages.ok)}
          <Box xcss={chevronIconWrapperStyles}>
            <ChevronDownIcon label="Open dropdown" size="small" />
          </Box>
        </Box>
      )}
      shouldRenderToParent
    >
      <Box xcss={dropdownMenuPanelStyles}>
        <DropdownItemGroup>
          <DropdownItem
            onClick={() => handleStatusChange(false)}
            isDisabled={isTestCaseUpdating}
          >
            <Tag text={formatMessage(messages.ok)} color="standard" />
          </DropdownItem>
          <DropdownItem
            onClick={() => handleStatusChange(true)}
            isDisabled={isTestCaseUpdating}
          >
            <Tag text={formatMessage(messages.flaky)} color="yellowLight" />
          </DropdownItem>
        </DropdownItemGroup>
      </Box>
    </DropdownMenu>
  );
};
